import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';
import { useSEO, getWebPageSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function AccelerateurPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.21daybusinesswebsite.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      getWebPageSchema(
        'FIRST CLIENTS FAST™ - Accélérateur acquisition client',
        'Système complet pour générer des leads qualifiés : mini-funnel, plan 30 jours et séquence email automatisée.',
        `${siteUrl}/accelerateur`
      ),
      getBreadcrumbSchema([
        { name: 'Accueil', url: '/' },
        { name: 'Accélérateur', url: '/accelerateur' }
      ])
    ]
  };

  useSEO({
    title: 'FIRST CLIENTS FAST™ - Accélérateur acquisition client | THE 21-DAY BUSINESS WEBSITE™',
    description: 'Système complet pour générer des leads qualifiés : mini-funnel de conversion, plan d\'action 30 jours et séquence email automatisée.',
    keywords: 'génération leads, acquisition client, funnel conversion, stratégie marketing, email marketing',
    canonical: `${siteUrl}/accelerateur`,
    schema
  });

  const features = [
    {
      icon: 'ri-rocket-line',
      title: 'Mini-funnel de conversion',
      description: 'Landing page + page de remerciement',
      details: [
        'Page de capture optimisée pour convertir',
        'Formulaire stratégique pour qualifier vos leads',
        'Page de remerciement avec prochaines étapes',
        'Design cohérent avec votre site principal'
      ]
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Plan 30 jours',
      description: 'Stratégie complète pour obtenir des leads',
      details: [
        'Actions quotidiennes détaillées',
        'Canaux d\'acquisition prioritaires',
        'Métriques à suivre',
        'Ajustements recommandés'
      ]
    },
    {
      icon: 'ri-mail-send-line',
      title: 'Séquence email en 3 messages',
      description: 'Automatisation pour nourrir vos leads',
      details: [
        'Email 1 : Bienvenue et présentation',
        'Email 2 : Valeur et expertise',
        'Email 3 : Appel à l\'action',
        'Templates prêts à intégrer dans votre outil'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main className="pt-20">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-br from-yellow-600 to-yellow-700 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/c0a9206cfdcf2b81b56377c3c15f2ee7.jpg')] bg-cover bg-center opacity-20"></div>
          
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-8">
              <i className="ri-flashlight-line"></i>
              <span>Option premium</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              FIRST CLIENTS FAST™
            </h1>
            <p className="text-2xl md:text-3xl mb-8 font-medium max-w-4xl mx-auto leading-tight">
              Vous ne repartez pas qu'avec un site :<br />
              vous repartez avec une machine à obtenir vos premiers clients.
            </p>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              Un système complet pour générer des leads qualifiés dès les premières semaines
            </p>
          </div>
        </section>

        {/* Features Detail */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Ce que vous obtenez
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                3 composantes essentielles pour accélérer votre acquisition client
              </p>
            </div>

            <div className="space-y-12">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="bg-slate-50 rounded-3xl p-8 md:p-12 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1">
                      <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${feature.icon} rounded-2xl mb-6`}>
                        <i className={`${feature.icon} text-3xl text-white`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-lg text-slate-600">
                        {feature.description}
                      </p>
                    </div>
                    
                    <div className="lg:col-span-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {feature.details.map((detail, detailIndex) => (
                          <div 
                            key={detailIndex}
                            className="flex items-start space-x-3 bg-white p-4 rounded-xl"
                          >
                            <i className="ri-checkbox-circle-fill text-xl text-amber-500 flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Pourquoi l'accélérateur fait la différence
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-2xl border border-slate-700">
                <i className="ri-speed-line text-4xl text-yellow-500 mb-4"></i>
                <h3 className="text-xl font-bold mb-3">Résultats rapides</h3>
                <p className="text-slate-300 leading-relaxed">
                  Ne perdez pas de temps à chercher comment attirer vos premiers clients. Tout est prêt pour vous.
                </p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-2xl border border-slate-700">
                <i className="ri-focus-3-line text-4xl text-yellow-500 mb-4"></i>
                <h3 className="text-xl font-bold mb-3">Stratégie complète</h3>
                <p className="text-slate-300 leading-relaxed">
                  Un plan d'action clair et détaillé pour les 30 premiers jours après le lancement.
                </p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-2xl border border-slate-700">
                <i className="ri-line-chart-line text-4xl text-yellow-500 mb-4"></i>
                <h3 className="text-xl font-bold mb-3">ROI maximisé</h3>
                <p className="text-slate-300 leading-relaxed">
                  Rentabilisez votre investissement plus rapidement en générant des leads dès le départ.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Prêt à accélérer votre croissance ?
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Ajoutez l'accélérateur First Clients Fast™ à votre projet
            </p>
            <Link to="/contact">
              <Button size="lg">
                Démarrer avec l'accélérateur
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

